// src/pages/Adverts.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  collection,
  getDocs,
  addDoc,
  doc,
  deleteDoc,
  Timestamp,
  query,
  orderBy,
  where,
  limit,
  writeBatch,
  collectionGroup,
  startAfter,
} from 'firebase/firestore';
import { db, storage } from '@root/firebase';
import {
  ref as storageRef,
  deleteObject,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { toast } from 'react-hot-toast';
import Layout from '@components/Layout';
import { useAuth } from '@context/AuthContext';
import ImageUploader from '@components/ImageUploader';
import ReactQuill from 'react-quill';
import { fetchCategories } from '@utils/MarketplaceCategories';
import ReadMoreTextBase from '@components/readmore/ReadMoreTextBase';
import GetViews from '@components/GetViews';
import Comments from '@components/Comments';
import TagInput from '@components/TagInput';
import Modal from '@/components/Modal';
import { dogBreeds } from '@utils/dogBreeds';

/* -------------------- shared helpers (mirrors Products) -------------------- */

// batch delete helper for any query (paginates)
async function deleteQueryBatch(db, q, batchSize = 50) {
  while (true) {
    const snap = await getDocs(query(q, limit(batchSize)));
    if (snap.empty) break;
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    if (snap.size < batchSize) break;
  }
}

// delete all docs in marketplace/{id}/{subName}
async function deleteSubcollection(db, parentId, subName, batchSize = 50) {
  const subRef = collection(db, `marketplace/${parentId}/${subName}`);
  await deleteQueryBatch(db, subRef, batchSize);
}

// (optional pattern) delete collectionGroup docs that reference parentId
async function deleteRelatedByParentId(
  db,
  cgName,
  parentIdField,
  parentId,
  batchSize = 50
) {
  const q = query(
    collectionGroup(db, cgName),
    where(parentIdField, '==', parentId)
  );
  await deleteQueryBatch(db, q, batchSize);
}

// slug for subcollection doc ids
const idFromVal = (v) =>
  String(v)
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-]/g, '');

// save array to subcollection as docId=value slug
async function saveArrayToSubcollection(
  db,
  parentPath,
  subName,
  arr,
  extra = {}
) {
  const batch = writeBatch(db);
  const uniq = Array.from(new Set((arr || []).filter(Boolean)));
  uniq.forEach((val) => {
    const ref = doc(collection(db, `${parentPath}/${subName}`), idFromVal(val));
    batch.set(ref, { value: val, ...extra, updatedAt: Timestamp.now() });
  });
  await batch.commit();
}

// read array from subcollection marketplace/{id}/{subName}
async function readSubcollectionValues(db, parentId, subName) {
  const qs = await getDocs(
    collection(db, `marketplace/${parentId}/${subName}`)
  );
  return qs.docs.map((d) => d.data()?.value).filter(Boolean);
}

const isValidHttpUrl = (str = '') => {
  try {
    const u = new URL(str);
    return ['http:', 'https:'].includes(u.protocol);
  } catch {
    return false;
  }
};
const withUTM = (url, source = 'smartdog', campaign = 'adverts') => {
  if (!isValidHttpUrl(url)) return url;
  const u = new URL(url);
  if (!u.searchParams.has('utm_source'))
    u.searchParams.set('utm_source', source);
  if (!u.searchParams.has('utm_medium'))
    u.searchParams.set('utm_medium', 'referral');
  if (!u.searchParams.has('utm_campaign'))
    u.searchParams.set('utm_campaign', campaign);
  return u.toString();
};

// Normalize dogBreeds into [{value,label}]
const normalizeBreeds = (list) =>
  list.map((b) =>
    typeof b === 'string'
      ? { value: b, label: b }
      : {
          value: b.value ?? b.id ?? b.name,
          label: b.label ?? b.name ?? b.value,
        }
  );
const BREED_ALL = 'ALL';

// Searchable Multi-select (same UX as Products)
function SearchableMultiSelect({
  options,
  values,
  onChange,
  placeholder = 'Type to search breeds…',
  showAllOption = true,
  allValue = BREED_ALL,
  allLabel = 'All Breeds',
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const normalized = useMemo(() => {
    const map = new Map();
    options.forEach((o) => {
      if (!o?.value) return;
      map.set(String(o.value), {
        value: String(o.value),
        label: o.label || String(o.value),
      });
    });
    return Array.from(map.values());
  }, [options]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = term
      ? normalized.filter((o) => o.label.toLowerCase().includes(term))
      : normalized.slice(0, 50);
    const selectedSet = new Set(values);
    list = list.filter((o) => !selectedSet.has(o.value));
    if (showAllOption && (!term || allLabel.toLowerCase().includes(term))) {
      if (!selectedSet.has(allValue))
        list = [{ value: allValue, label: allLabel }, ...list];
    }
    return list.slice(0, 50);
  }, [q, normalized, values, showAllOption, allLabel, allValue]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const commitAdd = (opt) => {
    if (!opt) return;
    if (opt.value === allValue) {
      onChange([allValue]);
    } else {
      const next = (values[0] === allValue ? [] : values).concat(opt.value);
      onChange(Array.from(new Set(next)));
    }
    setQ('');
    setOpen(true);
    setHighlight(0);
    inputRef.current?.focus();
  };

  const removeValue = (val) => onChange(values.filter((v) => v !== val));

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) setOpen(true);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitAdd(filtered[highlight]);
    } else if (e.key === 'Backspace' && !q) {
      if (values.length) removeValue(values[values.length - 1]);
    }
  };

  return (
    <div
      ref={rootRef}
      className='form-control p-2'
      style={{ position: 'relative' }}
    >
      <div className='d-flex flex-wrap gap-2'>
        {values.map((v) => (
          <span key={v} className='badge bg-light text-dark border'>
            {v === allValue
              ? allLabel
              : normalized.find((o) => o.value === v)?.label || v}
            <button
              type='button'
              className='btn btn-sm btn-link text-decoration-none ms-1 p-0'
              onClick={() => removeValue(v)}
              aria-label='Remove'
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          style={{ minWidth: 160, border: 'none', outline: 'none', flex: 1 }}
        />
      </div>

      {open && (
        <div
          className='bg-white border rounded shadow-sm mt-2'
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {filtered.length === 0 ? (
            <div className='px-3 py-2 text-muted'>No matches</div>
          ) : (
            filtered.map((o, i) => (
              <button
                key={o.value}
                type='button'
                className={`dropdown-item text-start ${
                  i === highlight ? 'active' : ''
                }`}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commitAdd(o)}
              >
                {o.label}
                {o.value === BREED_ALL && (
                  <span className='ms-2 badge bg-secondary'>All</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const decideAdvertStatus = (ad) => {
  if (ad?.status) return ad.status; // trust stored status if present

  const now = new Date();
  const toDate = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
  const start = toDate(ad?.startDate);
  const end = toDate(ad?.endDate);

  if (!start) return 'inactive';
  if (start > now) return 'future';
  if (end && end < now) return 'inactive';
  return 'active';
};

/* --------------------------------- Page ---------------------------------- */

function Adverts() {
  const { globalUser, globalData, LogError } = useAuth();

  // categories
  const [categories, setCategories] = useState([]);
  const [catMap, setCatMap] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const subCategories = useMemo(
    () => categories.filter((c) => c.parent === selectedCategory),
    [categories, selectedCategory]
  );

  // lists
  const [adverts, setAdverts] = useState({
    active: [],
    inactive: [],
    future: [],
  });
  const [loading, setLoading] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);

  // form
  const uploaderRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    subTitle: '',
    startDate: '',
    endDate: '',
    description: '',
  });

  // breeds + geo + tags
  const BREEDS = useMemo(() => normalizeBreeds(dogBreeds), []);
  const [selectedBreeds, setSelectedBreeds] = useState([BREED_ALL]);
  const GEO_OPTIONS = ['Local', 'Regional', 'State', 'National'];
  const [selectedGeo, setSelectedGeo] = useState([]);
  const toggleGeo = (label) =>
    setSelectedGeo((prev) =>
      prev.includes(label) ? prev.filter((g) => g !== label) : [...prev, label]
    );
  const [tags, setTags] = useState([]);

  // delete modal state
  const [deleteId, setDeleteId] = useState(null);
  const [deleteImage, setDeleteImage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /* ---------------------- load categories + map ---------------------- */
  const loadCategories = useCallback(async () => {
    const fetched = await fetchCategories(); // [{id,name,parent}]
    setCategories(fetched);
    setCatMap(Object.fromEntries(fetched.map((c) => [c.id, c])));
  }, []);

  useEffect(() => {
    loadCategories().catch(() => toast.error('Error fetching categories.'));
  }, [loadCategories]);

  /* ---------------------- queries ---------------------- */

  const hydrateWithSubs = async (docs) => {
    return Promise.all(
      docs.map(async (d) => {
        const data = d.data();
        const [breedsArr, geoArr, tagsArr] = await Promise.all([
          readSubcollectionValues(db, d.id, '_meta')
            .then((v) => v)
            .catch(() => []), // not used, but kept slot for symmetry
          readSubcollectionValues(db, d.id, 'geography').catch(() => []),
          readSubcollectionValues(db, d.id, 'tags').catch(() => []),
        ]);
        const breeds = await readSubcollectionValues(db, d.id, 'breeds').catch(
          () => []
        );
        return {
          id: d.id,
          ...data,
          categoryName: data.categoryId
            ? catMap[data.categoryId]?.name || ''
            : '',
          subcategoryName: data.subCategoryId
            ? catMap[data.subCategoryId]?.name || ''
            : '',
          breeds,
          geography: geoArr,
          tags: tagsArr,
        };
      })
    );
  };

  const loadAdverts = useCallback(async () => {
    if (!globalUser?.uid) return;
    setLoading(true);
    try {
      // 1) Fetch *all* adverts by this user (keep query simple → fewer index needs)
      const qs = await getDocs(
        query(
          collection(db, 'marketplace'),
          where('uid', '==', globalUser.uid) // single equality filter
          // (we'll filter type and status in JS)
        )
      );

      // 2) Only adverts, then hydrate subcollections
      const onlyAdvertsDocs = qs.docs.filter(
        (d) => d.data()?.type === 'adverts'
      );
      const rows = await hydrateWithSubs(onlyAdvertsDocs);

      // 3) Attach category/subcategory names if catMap is ready
      const withNames = rows.map((r) => ({
        ...r,
        categoryName: r.categoryId ? catMap[r.categoryId]?.name || '' : '',
        subcategoryName: r.subCategoryId
          ? catMap[r.subCategoryId]?.name || ''
          : '',
      }));

      // 4) Bucket by status (derive if missing)
      const buckets = { active: [], inactive: [], future: [] };
      withNames.forEach((ad) => {
        const st = decideAdvertStatus(ad);
        buckets[st] = (buckets[st] || []).concat(ad);
      });

      setAdverts({
        active: buckets.active || [],
        inactive: buckets.inactive || [],
        future: buckets.future || [],
      });
    } catch (e) {
      console.error('[Adverts] load error', e);
      toast.error('Failed to load adverts.');
    } finally {
      setLoading(false);
    }
  }, [globalUser?.uid, catMap]); // re-run when uid or category names change

  useEffect(() => {
    loadCategories().catch(() => toast.error('Error fetching categories.'));
  }, [loadCategories]);

  // Load adverts as soon as we have a uid; names will fill in once catMap arrives
  useEffect(() => {
    loadAdverts();
  }, [loadAdverts]);

  /* ---------------------- form handlers ---------------------- */
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSubCategory('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
  };

  const handleFormSubmit = async () => {
    if (
      !formData.title ||
      !formData.subTitle ||
      !formData.startDate ||
      !formData.endDate ||
      !selectedCategory ||
      !selectedSubCategory
    ) {
      toast.error('Please fill out all required fields.');
      return;
    }

    try {
      setLoading(true);

      // upload image
      const selectedFile = uploaderRef.current?.getSelectedFile();
      let uploadedImage = null;
      if (selectedFile) {
        const rand =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const cleanName = selectedFile.name || `${formData.title}.jpg`;
        const path = `users/${globalData.uid}/marketplace/${rand}-${cleanName}`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, selectedFile);
        uploadedImage = await getDownloadURL(fileRef);
      }

      // timestamps & status
      const startDateTimestamp = Timestamp.fromDate(
        new Date(formData.startDate)
      );
      const endDateTimestamp = Timestamp.fromDate(new Date(formData.endDate));

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(formData.startDate);
      startDate.setHours(0, 0, 0, 0);
      const status = startDate > today ? 'future' : 'active';

      // ROOT doc — no breeds/geography/tags (save under subcollections)
      const rootDoc = {
        type: 'adverts',
        title: formData.title,
        subTitle: formData.subTitle,
        description: formData.description,
        startDate: startDateTimestamp,
        endDate: endDateTimestamp,
        image: uploadedImage || null,
        status,
        uid: globalData.uid,
        categoryId: selectedCategory,
        subCategoryId: selectedSubCategory,
        createdAt: Timestamp.now(),
      };

      const collRef = collection(db, 'marketplace');
      const docRef = await addDoc(collRef, rootDoc);

      // subcollections
      const parentPath = `marketplace/${docRef.id}`;
      const breedsToSave =
        selectedBreeds?.[0] === BREED_ALL ? [BREED_ALL] : [...selectedBreeds];
      await Promise.all([
        saveArrayToSubcollection(db, parentPath, '_meta', ['adverts']), // optional flag marker
        saveArrayToSubcollection(db, parentPath, 'breeds', breedsToSave),
        saveArrayToSubcollection(
          db,
          parentPath,
          'geography',
          selectedGeo || []
        ),
        saveArrayToSubcollection(db, parentPath, 'tags', tags || []),
      ]);

      // read back
      const [breedsArr, geoArr, tagsArr] = await Promise.all([
        readSubcollectionValues(db, docRef.id, 'breeds'),
        readSubcollectionValues(db, docRef.id, 'geography'),
        readSubcollectionValues(db, docRef.id, 'tags'),
      ]);

      // optimistic UI into correct bucket
      setAdverts((prev) => {
        const next = { ...prev };
        const item = {
          id: docRef.id,
          ...rootDoc,
          categoryName: catMap[selectedCategory]?.name || '',
          subcategoryName: catMap[selectedSubCategory]?.name || '',
          breeds: breedsArr,
          geography: geoArr,
          tags: tagsArr,
        };
        next[status] = [item, ...(next[status] || [])];
        return next;
      });

      // reset form
      setFormData({
        title: '',
        subTitle: '',
        startDate: '',
        endDate: '',
        description: '',
      });
      setSelectedCategory('');
      setSelectedSubCategory('');
      setSelectedBreeds([BREED_ALL]);
      setSelectedGeo([]);
      setTags([]);
      toast.success('Advert created.');
    } catch (error) {
      toast.error('Error creating advert.');
      LogError('Error', {
        error: {
          message: error.message || 'Unknown error',
          stack: error.stack || 'No stack trace available',
          name: error.name || 'Error',
        },
        userName: globalData.name || 'No Name',
        uid: globalUser.uid || 'No ID',
        functionCode: 'handleFormSubmit',
        page: 'SMART Advert',
      });
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------- delete with recursive cleanup ---------------------- */
  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    setIsDeleting(true);
    try {
      // 1) try delete storage image
      if (deleteImage) {
        try {
          const fileRef = storageRef(storage, deleteImage);
          await deleteObject(fileRef);
        } catch {
          /* ignore if not a storage url */
        }
      }

      // 2) subcollections to clear
      const SUBS = [
        '_meta',
        'breeds',
        'geography',
        'tags',
        'comments',
        'views',
        'likes',
        'reactions',
      ];
      await Promise.all(
        SUBS.map((name) => deleteSubcollection(db, deleteId, name))
      );

      // 3) (optional) if you store comments/views in CG with parentId
      // await deleteRelatedByParentId(db, 'comments', 'parentId', deleteId);
      // await deleteRelatedByParentId(db, 'views', 'parentId', deleteId);

      // 4) root
      await deleteDoc(doc(db, 'marketplace', deleteId));

      // 5) UI
      setAdverts((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => {
          next[k] = (next[k] || []).filter((a) => a.id !== deleteId);
        });
        return next;
      });
      toast.success('Advert and related data removed.');
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete advert.');
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
      setDeleteImage(null);
    }
  };

  /* ---------------------- UI renderers ---------------------- */

  const renderAdverts = (advertList) => {
    if (!advertList?.length) {
      return (
        <div className='col-sm-3'>
          <div className='alert alert-danger left-icon-big alert-dismissible fade show'>
            <button
              type='button'
              className='btn-close'
              data-bs-dismiss='alert'
              aria-label='btn-close'
            >
              <span>
                <i className='mdi mdi-btn-close'></i>
              </span>
            </button>
            <div className='media'>
              <div className='alert-left-icon-big'>
                <span>
                  <i className='mdi mdi-help-circle-outline'></i>
                </span>
              </div>
              <div className='media-body'>
                <h5 className='mt-1 mb-2'>No Advertisements!</h5>
                <p className='mb-0'>You have no advertisements to show.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return advertList.map((advert) => (
      <div key={advert.id} className='col-md-6 col-xl-3'>
        <div className='card'>
          {advert.image ? (
            <img
              className='card-img-top img-fluid'
              src={advert.image}
              alt={advert.title}
            />
          ) : null}

          <div className='card-body'>
            <h4 className='card-title'>{advert.title || 'Untitled Advert'}</h4>
            <h6>{advert.subTitle || ''}</h6>
            <ReadMoreTextBase
              text={advert.description || 'No description provided.'}
              wordLimit={3}
              readMore
            />

            {/* Breeds */}
            {advert.breeds?.length ? (
              <p className='mt-2 mb-1'>
                Breeds:{' '}
                {advert.breeds[0] === BREED_ALL ? (
                  <span className='badge bg-light text-dark border'>
                    All Breeds
                  </span>
                ) : (
                  advert.breeds.map((b) => (
                    <span
                      key={b}
                      className='badge bg-light text-dark border me-1'
                    >
                      {b}
                    </span>
                  ))
                )}
              </p>
            ) : null}

            {/* Geography */}
            {advert.geography?.length ? (
              <p className='mb-1'>
                Geography:{' '}
                {advert.geography.map((g) => (
                  <span
                    key={g}
                    className='badge bg-light text-dark border me-1'
                  >
                    {g}
                  </span>
                ))}
              </p>
            ) : null}

            {/* Actions */}
            <div className='row row-cols-3 g-2 mt-2'>
              <div className='col d-flex justify-content-center'>
                <GetViews
                  docPath={`marketplace/${advert.id}`}
                  buttonSize='xs'
                />
              </div>
              <div className='col d-flex justify-content-center'>
                <Comments
                  parentId={advert.id}
                  collectionPath='marketplace'
                  showCommentLabel={false}
                />
              </div>
              <div className='col d-flex justify-content-center'>
                <div className='tooltip-container'>
                  <button
                    type='button'
                    className='reaction-btn reaction-action'
                    title='Delete Advert'
                    data-bs-toggle='modal'
                    data-bs-target='#confirmDelete'
                    onClick={() => {
                      setDeleteId(advert.id);
                      setDeleteImage(advert.image || null);
                    }}
                  >
                    <i className='fas fa-trash reaction-icon' />
                  </button>
                  <span className='tooltip'>Delete {advert.title}</span>
                </div>
              </div>
            </div>
          </div>

          <div className='card-footer bg-transparent border-top text-muted'>
            <small className='text-muted'>
              Category: <mark>{advert.categoryName || 'Unknown'}</mark> {' > '}{' '}
              <mark>{advert.subcategoryName || 'Unknown'}</mark>
            </small>
            <br />
            <small className='text-muted'>
              Start Date:{' '}
              {advert.startDate?.toDate
                ? advert.startDate.toDate().toLocaleDateString()
                : ''}
            </small>
            <br />
            <small className='text-muted'>
              End Date:{' '}
              {advert.endDate?.toDate
                ? advert.endDate.toDate().toLocaleDateString()
                : ''}
            </small>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <Layout>
      <div className='row'>
        <div className='col-xl-12 col-lg-8'>
          <div className='card'>
            <div className='card-body'>
              <div className='row'>
                <div className='col-md-6'>
                  <h5>Showing result for Adverts</h5>
                  <ol className='breadcrumb p-0 bg-transparent mb-2'>
                    <li className='breadcrumb-item'>
                      <a href='#' onClick={(e) => e.preventDefault()}>
                        Marketplace
                      </a>
                    </li>
                    <li className='breadcrumb-item active'>Adverts</li>
                  </ol>
                </div>
                <div className='col-md-6'>
                  <div className='form-inline float-md-end'>
                    <div className='search-box ms-2'>
                      <div className='position-relative'>
                        <input
                          type='text'
                          className='form-control bg-light border-light rounded'
                          placeholder='Search...'
                        />
                        <i className='bx bx-search search-icon'></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <ul className='nav nav-tabs nav-tabs-custom mt-3 mb-2 ecommerce-sortby-list'>
                <li className='nav-item'>
                  <a
                    className='nav-link disabled fw-medium'
                    href='#'
                    tabIndex='-1'
                    aria-disabled='true'
                  >
                    Sort by:
                  </a>
                </li>
                <li className='nav-item'>
                  <a
                    className='nav-link active'
                    data-bs-toggle='tab'
                    href='#active'
                    role='tab'
                    aria-selected='false'
                  >
                    Active
                  </a>
                </li>
                <li className='nav-item'>
                  <a
                    className='nav-link'
                    data-bs-toggle='tab'
                    href='#inactive'
                    role='tab'
                    aria-selected='false'
                  >
                    Inactive
                  </a>
                </li>
                <li className='nav-item'>
                  <a
                    className='nav-link'
                    data-bs-toggle='tab'
                    href='#future'
                    role='tab'
                    aria-selected='true'
                  >
                    Future
                  </a>
                </li>
                <li className='nav-item'>
                  <a
                    className='nav-link'
                    data-bs-toggle='tab'
                    href='#advert'
                    role='tab'
                    aria-selected='true'
                  >
                    Add New Advert
                  </a>
                </li>
              </ul>

              <div className='tab-content p-3 text-muted'>
                <div className='tab-pane active' id='active' role='tabpanel'>
                  <div className='row'>{renderAdverts(adverts.active)}</div>
                </div>
                <div className='tab-pane' id='inactive' role='tabpanel'>
                  <div className='row'>{renderAdverts(adverts.inactive)}</div>
                </div>
                <div className='tab-pane' id='future' role='tabpanel'>
                  <div className='row'>{renderAdverts(adverts.future)}</div>
                </div>

                {/* Add new */}
                <div className='tab-pane' id='advert' role='tabpanel'>
                  <div className='row'>
                    {/* Uploader */}
                    <div className='col-12'>
                      <ImageUploader
                        ref={uploaderRef}
                        uid={globalData.uid}
                        onFileSelect={() => {}}
                        onUpload={() => {}}
                      />
                    </div>

                    {/* Category & subcategory */}
                    <div className='col-4 form-floating mt-3'>
                      <select
                        className='form-select native-select'
                        id='parent-category'
                        value={selectedCategory}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                      >
                        <option value=''>Select a category</option>
                        {categories
                          .filter((cat) => !cat.parent)
                          .map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                      </select>
                      <label htmlFor='parent-category'>Parent Category</label>
                    </div>

                    <div className='col-4 form-floating mt-3'>
                      <select
                        id='child-category'
                        className='form-select native-select'
                        value={selectedSubCategory}
                        onChange={(e) => setSelectedSubCategory(e.target.value)}
                        disabled={!selectedCategory}
                      >
                        <option value=''>Select a sub-category</option>
                        {subCategories.map((subCat) => (
                          <option key={subCat.id} value={subCat.id}>
                            {subCat.name}
                          </option>
                        ))}
                      </select>
                      <label htmlFor='child-category'>Sub Category</label>
                    </div>

                    {/* Title / subtitle */}
                    <div className='col-4 form-floating mt-3'>
                      <input
                        className='form-control'
                        type='text'
                        name='title'
                        value={formData.title}
                        onChange={handleInputChange}
                      />
                      <label>Advert Title</label>
                    </div>

                    <div className='col-4 form-floating mt-3'>
                      <input
                        className='form-control'
                        type='text'
                        name='subTitle'
                        value={formData.subTitle}
                        onChange={handleInputChange}
                      />
                      <label>Advert Sub Title</label>
                    </div>

                    {/* Dates */}
                    <div className='col-4 form-floating mt-3'>
                      <input
                        className='form-control'
                        type='date'
                        name='startDate'
                        value={formData.startDate}
                        onChange={handleInputChange}
                      />
                      <label>Advert Start Date</label>
                    </div>

                    <div className='col-4 form-floating mt-3'>
                      <input
                        className='form-control'
                        type='date'
                        name='endDate'
                        value={formData.endDate}
                        onChange={handleInputChange}
                      />
                      <label>Advert End Date</label>
                    </div>

                    {/* Description */}
                    <div className='col-12 mt-3'>
                      <ReactQuill
                        value={formData.description}
                        onChange={(value) =>
                          setFormData((f) => ({ ...f, description: value }))
                        }
                      />
                    </div>

                    {/* Breeds */}
                    <div className='col-md-8 mt-3'>
                      <label className='form-label d-flex justify-content-between'>
                        <span>Dog Breeds</span>
                        <button
                          type='button'
                          className='btn btn-link btn-sm p-0'
                          onClick={() => setSelectedBreeds([])}
                        >
                          Clear
                        </button>
                      </label>

                      <SearchableMultiSelect
                        options={[...BREEDS]}
                        values={selectedBreeds}
                        onChange={(arr) =>
                          arr.includes(BREED_ALL)
                            ? setSelectedBreeds([BREED_ALL])
                            : setSelectedBreeds(arr)
                        }
                        showAllOption
                        allValue={BREED_ALL}
                        allLabel='All Breeds'
                        placeholder='Search breeds…'
                      />

                      {/* Hidden native select to play nice with your CSS override */}
                      <select
                        className='form-select d-none'
                        multiple
                        value={selectedBreeds}
                        readOnly
                      >
                        <option value={BREED_ALL}>All Breeds</option>
                        {BREEDS.map((b) => (
                          <option key={b.value} value={b.value}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Geography */}
                    <div className='col-md-4 mt-3'>
                      <label className='form-label'>
                        Geography (where advertised)
                      </label>
                      <div className='row'>
                        {GEO_OPTIONS.map((g, i) => {
                          const id = `geo-${i}`;
                          return (
                            <div key={g} className='col-6'>
                              <div className='form-check custom-checkbox mb-2'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={id}
                                  checked={selectedGeo.includes(g)}
                                  onChange={() => toggleGeo(g)}
                                />
                                <label
                                  className='form-check-label'
                                  htmlFor={id}
                                >
                                  {g}
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Tags */}
                    <div className='col-12 mt-3'>
                      <label className='form-label'>Tags (optional)</label>
                      <TagInput
                        id='advertTags'
                        tags={tags}
                        onChange={setTags}
                      />
                    </div>

                    {/* Submit */}
                    <div className='col text-end mt-3'>
                      <button
                        className='btn btn-success'
                        onClick={handleFormSubmit}
                      >
                        {loading ? (
                          <>
                            <i className='bx bx-loader bx-spin font-size-16 align-middle me-2'></i>
                            Registering...
                          </>
                        ) : (
                          <>
                            <i className='bx bx-file me-1'></i> Register
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Delete Modal */}
      <Modal
        id='confirmDelete'
        title='Remove This Advert?'
        onConfirm={handleConfirmDelete}
        confirmText='Yes, delete'
        cancelText='Cancel'
        loading={isDeleting}
        loadingText='Deleting...'
        size='medium'
      >
        <p>Are you sure you want to delete this advert?</p>
      </Modal>
    </Layout>
  );
}

export default Adverts;
